import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useState} from 'react';
import toast from '$utils/toast';
import {ToastType} from '$components/Toast/constant';
import {handleErrorResponse} from '$utils/axios';
import {
  putPatientCare,
  PutPatientCarePayload,
} from '$api/patientList/patientDetails';
import {
  postConsultationOutcome,
  ConsultationOutcomePayload,
} from '$api/frontdesk';

export function useCreatePatientCare(onSuccess: () => void) {
  const [disableSubmit, setDisableSubmit] = useState(false);
  const queryClient = useQueryClient();

  const {mutate, isPending} = useMutation({
    mutationKey: ['patient-details-update-patient-care'],
    mutationFn: putPatientCare,
    onSuccess: data => {
      toast(ToastType.Success, 'Update patient care', data.message);
      queryClient.invalidateQueries({
        queryKey: ['patient-details'],
      });
      onSuccess();
    },
    onError: error => handleErrorResponse(error, 'Update Patient Care'),
    onSettled: () => setDisableSubmit(false),
  });

  const sendCreatePatientCareRequest = (payload: PutPatientCarePayload) => {
    setDisableSubmit(true);
    mutate(payload);
  };

  return {sendCreatePatientCareRequest, isPending, disableSubmit};
}

export function useConfirmConsultationOutcome(
  onSuccess: () => void,
  appointmentId: number
) {
  const [disableSubmit, setDisableSubmit] = useState(false);
  const queryClient = useQueryClient();

  const {mutate, isPending} = useMutation({
    mutationKey: ['confirm-consultation-outcome'],
    mutationFn: postConsultationOutcome,
    onSuccess: data => {
      toast(ToastType.Success, 'Consultation outcome', data.message);
      queryClient.invalidateQueries({
        queryKey: ['appointment-detail', appointmentId],
      });
      onSuccess();
    },
    onError: error => handleErrorResponse(error, 'Consultation outcome'),
    onSettled: () => setDisableSubmit(false),
  });

  const sendConfirmConsultationOutcomeRequest = (
    payload: ConsultationOutcomePayload
  ) => {
    setDisableSubmit(true);
    mutate(payload);
  };

  return {sendConfirmConsultationOutcomeRequest, isPending, disableSubmit};
}
